import { CheckCircle2, FileText, Hash, ShieldAlert, Workflow } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";

export type CommandTraceViewProps = {
  traceId: string;
  status: string;
  routeName: string;
  commandName: string;
  policyReasons: string[];
  evidenceRefs: string[];
  payloadHash: string;
};

export function CommandTraceView({
  traceId,
  status,
  routeName,
  commandName,
  policyReasons,
  evidenceRefs,
  payloadHash,
}: CommandTraceViewProps) {
  return (
    <div className="rounded-md border border-border bg-card p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-medium">
          <Workflow size={15} />
          {commandName.replaceAll("_", " ")}
        </div>
        <Badge variant={statusVariant(status)}>{status.replaceAll("_", " ")}</Badge>
      </div>
      <div className="mt-2 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
        <TraceFact icon={<ShieldAlert size={13} />} label="Route" value={routeName} />
        <TraceFact icon={<Hash size={13} />} label="Trace" value={traceId} />
        <TraceFact icon={<FileText size={13} />} label="Payload" value={payloadHash} />
        <TraceFact icon={<CheckCircle2 size={13} />} label="Evidence" value={`${evidenceRefs.length} refs`} />
      </div>
      {policyReasons.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1">
          {policyReasons.slice(0, 4).map((reason) => (
            <Badge key={reason} variant="warn">
              {reason}
            </Badge>
          ))}
        </div>
      ) : null}
      {evidenceRefs.length > 0 ? (
        <div className="mt-3 text-xs text-muted-foreground">{evidenceRefs.slice(0, 4).join(", ")}</div>
      ) : null}
    </div>
  );
}

function TraceFact({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="text-muted-foreground">{icon}</span>
      <span className="shrink-0">{label}</span>
      <span className="truncate font-mono text-foreground">{value}</span>
    </div>
  );
}

function statusVariant(status: string): "good" | "warn" | "bad" | "info" | "neutral" {
  if (["accepted", "succeeded", "executed", "dry_run"].includes(status)) return "good";
  if (["approval_required", "queued", "pending"].includes(status)) return "warn";
  if (["blocked", "failed", "rejected"].includes(status)) return "bad";
  if (["running", "drafted"].includes(status)) return "info";
  return "neutral";
}
